import { Component, OnInit, ViewChild, ElementRef } from "@angular/core";
import { WizardStep } from "../../../wizard/wizard.component";
import {
  FormGroup,
  FormBuilder,
  FormArray,
  AbstractControl,
  ControlValueAccessor,
  Validator,
  Validators,
} from "@angular/forms";
import { BehaviorSubject } from "rxjs";
import { NotificationToastService } from "../../../services/notification-toast.service";
import {
  ServiceReservation,
  ServiceReservationInfo,
} from "src/app/model/service-reservation";
import { ReservationWizardService } from "../../../services/wizard/reservation/reservation.wizard.service";
import { Entity } from "../../../model/entity";
import { ReferringPhysicianCompleteComponent } from "../../autocomplete/referring-physician/referring-physician-autocomplete.component";
import { ReferringPhysicianFilter } from "src/app/model/referring-physician";
import { AuthService } from "../../../services/auth.service";
import { MedicalOrderScann } from "src/app/model/medical-order-scann";

@Component({
  selector: "app-reservation-order-component",
  templateUrl: "./reservation-order.component.html",
  styleUrls: ["./reservation-order.component.css"],
})
export class ReservationOrderComponent implements OnInit, WizardStep {

  showScann  =false;
  credentialRequired: boolean;
  tokenRequired: boolean;
  preAuthorization: boolean;
  preAuthorizationRequired: boolean;
  loading: boolean;
  isInit: boolean;
  activated: boolean;
  selectIndex: number;
  canViewReferringPhysician: boolean;
  canViewMedicaOrderDate: boolean;
  public reservationOrderFormGroup: FormGroup;
  practicesRegionDataSource = new BehaviorSubject<AbstractControl[]>([]);
  displayPracticesRegionColumns = ["Name"];
  @ViewChild("referringPhysician", { static: false })
  referringPhysician: ReferringPhysicianCompleteComponent;
  validityDays = "";

  constructor(
    private notificationToastService: NotificationToastService,
    private formBuilder: FormBuilder,
    private authService: AuthService,
    public reservationWizardService: ReservationWizardService
  ) {
    this.reservationOrderFormGroup = this.formBuilder.group({
      ServiceReservations: this.formBuilder.array([]),
    });

    this.selectIndex = 0;
  }

  get patientId() {
    return this.reservationWizardService.reservation.Patient ? this.reservationWizardService.reservation.Patient.Id : null  ;
  }

  onMedicalOrderScannsChange(medicalOrderScanns: MedicalOrderScann[]) {
    this.reservationWizardService.reservation.MedicalOrderScanns = medicalOrderScanns;
  }


  get serviceReservationsArrayGroup() {
    return <FormArray>this.reservationOrderFormGroup.get("ServiceReservations");
  }
  get completed(): boolean {
    return this.isValid();
  }

  get valid(): boolean {
    return this.completed;
  }

  get f() {
    return this.reservationOrderFormGroup.controls;
  }
  get fSelectedServiceReservation() {
    return this.serviceReservationsArrayGroup.controls[this.selectIndex];
  }

  isValid(): boolean {
    let valid = true;

    if (!this.activated) {
      valid = false;
    }

    if (!this.reservationOrderFormGroup) {
      valid = false;
    }

    this.serviceReservationsArrayGroup.controls.forEach((control) => {
      if (control.invalid) {
        valid = false;
      }
    });

    if (this.reservationOrderFormGroup.invalid) {
      valid = false;
    }

    return valid;
  }

  ngOnInit(): void {
    this.isInit = true;
  }

  async activate() {

    if (!this.activated) {
      this.showScann = this.reservationWizardService.reservation.MedicalOrderScanns.length === 0;
    }

    this.activated = true;
    this.reservationOrderFormGroup.controls.ServiceReservations =
      this.formBuilder.array([]);
    this.practicesRegionDataSource.next(
      this.serviceReservationsArrayGroup.controls
    );
    const serviceReservations =
      this.reservationWizardService.getAllServiceReservations;
    this.setValidityDays(0);

    this.canViewReferringPhysician =
      await this.authService.canViewReferringPhysician();
    this.canViewMedicaOrderDate =
      await this.authService.canViewMedicaOrderDate();
    let entity = 0;



    serviceReservations.forEach(async (serviceReservation) => {
      if (
        !this.preAuthorizationRequired &&
        entity !== serviceReservation.Entity.Id
      ) {
        this.preAuthorizationRequired =
          await this.reservationWizardService.getEntityPreAuthorizeInReservation(
            serviceReservation.Entity.Id
          );

        if (!this.tokenRequired) {
          this.tokenRequired = serviceReservation.Entity.TokenRequired;
        }

        if (!this.credentialRequired) {
          this.credentialRequired = serviceReservation.Entity.CredentialRequired;
        }
      }

      entity = serviceReservation.Entity.Id;

      const row = this.formBuilder.group({
        DateOrder: [
          serviceReservation.DateOrder,
          this.canViewMedicaOrderDate ? Validators.required : null,
        ],
        DateAuthorization: [serviceReservation.DateAuthorization],
        Diagnostic: [
          serviceReservation.Diagnostic,
          serviceReservation.diagnosticRequired() ? Validators.required : null,
        ],
        ReferringPhysician: [serviceReservation.ReferringPhysician],
        Observation: [serviceReservation.Observation],
        PatientObservation: [serviceReservation.PatientObservation],
        CredentialNumber: [serviceReservation.CredentialNumber],
        SecurityCodeCredentialNumber: [serviceReservation.SecurityCodeCredentialNumber],
        Index: [serviceReservation.Index],
        Center: [serviceReservation.Center],
        Entity: [serviceReservation.Entity],
        Plan: [serviceReservation.Plan],
        MedicalAppointmentOriginalId: [
          serviceReservation.MedicalAppointmentOriginalId,
        ],
        ReAppointmentDuration: [serviceReservation.ReAppointmentDuration],
        PracticeRegionReservation: [
          serviceReservation.PracticeRegionReservation,
        ],
        PracticesRegionReservation: [
          serviceReservation.PracticesRegionReservation,
        ],
        PracticesRegionAditionalReservation: [
          serviceReservation.PracticesRegionAditionalReservation,
        ],
        SetSuppliesReservation: [serviceReservation.SetSuppliesReservation],
        PrincipalPracticeRegionReservationName: [
          serviceReservation.principalPracticeRegionReservationName(),
        ],
      });

      this.serviceReservationsArrayGroup.push(row);
      this.practicesRegionDataSource.next(
        this.serviceReservationsArrayGroup.controls
      );
    });

    // cuando cambian los valores ya lo impacto en las reservas
    this.serviceReservationsArrayGroup.valueChanges.subscribe(() => {
      this.setServiceReservation();
    });
  }

  setIndex(index: number): void {
    this.selectIndex = index;
    this.setValidityDays(index);
  }

  private setValidityDays(index: number) {
    const serviceReservation =
      this.reservationWizardService.getAllServiceReservations[index];
    this.validityDays = "";
    if (serviceReservation.Entity.AthorizationValidity) {
      this.validityDays =
        "(" + serviceReservation.Entity.AthorizationValidityDay + ")";
    } else {
      this.validityDays =
        "(" + serviceReservation.Entity.MedicalOrderValidityDay + ")";
    }
  }

  onPatientObservationChange(observation: string) {
    this.serviceReservationsArrayGroup.controls.forEach((control) => {
      control.patchValue({ PatientObservation: observation });
    });
  }

  onDiagnosticChange(dianostic: string) {
    this.serviceReservationsArrayGroup.controls.forEach((control) => {
      control.patchValue({ Diagnostic: dianostic });
    });
  }

  onDateChange(value: Date) {
    this.serviceReservationsArrayGroup.controls.forEach((control) => {
      if (!control.value.DateOrder) {
        control.patchValue({ DateOrder: value });
      }
      const entity = <Entity>control.value.Entity;
      if (entity.AthorizationValidity) {
        control.patchValue({ DateAuthorization: control.value.DateOrder });
      }
    });
  }

  onObservationChange(observation: string) {
    this.serviceReservationsArrayGroup.controls.forEach((control) => {
      control.patchValue({ Observation: observation });
    });
  }

  onCredentialNumberChange(credentialNumber: string) {
    this.serviceReservationsArrayGroup.controls.forEach((control) => {
      control.patchValue({ CredentialNumber: credentialNumber });
    });
  }

  onSecurityCodeCredentialNumberChange(token: string) {
    this.serviceReservationsArrayGroup.controls.forEach((control) => {
      control.patchValue({ SecurityCodeCredentialNumber: token });
    });
  }

  get getIndex() {
    return this.selectIndex;
  }

  async executePreAuthorization() {
    try {
      this.loading = true;
      this.onObservationChange(
        this.serviceReservationsArrayGroup.controls[0].value.Observation
      );
      this.onCredentialNumberChange(
        this.serviceReservationsArrayGroup.controls[0].value.CredentialNumber
      );

      this.onSecurityCodeCredentialNumberChange(
        this.serviceReservationsArrayGroup.controls[0].value.SecurityCodeCredentialNumber
      );

      this.onDiagnosticChange(
        this.serviceReservationsArrayGroup.controls[0].value.Diagnostic
      );
      this.onDateChange(
        this.serviceReservationsArrayGroup.controls[0].value.DateOrder
      );
      this.setServiceReservation();
      this.preAuthorization = true;
      const info =
        await this.reservationWizardService.executePreAuthorization();
      let isOk = true;
      info.forEach((info) => {
        if (info.NoValidate) {
          isOk = false;
          this.notificationToastService.info(
            "Error al pre autorizar <br>" + info.Message,
            "Reserva de turno",
            true,
            false,
            true
          );
        }
      });

      if (isOk) {
        this.notificationToastService.success(
          "Todas las prestaciones se encuentran autorizadas",
          "Reserva de turno"
        );
      }
    } catch (err) {
      this.notificationToastService.error(
        "Error al pre autorizar " + JSON.stringify(err.error),
        "Reserva de turno"
      );
    } finally {
      this.loading = false;
    }
  }

  displayPracticeName(index: number): string {
    return this.serviceReservationsArrayGroup.controls[index].value
      .PrincipalPracticeRegionReservationName;
  }

  isAthorizationValidity(index: number): string {
    return this.serviceReservationsArrayGroup.controls[index].value
      .IsAthorizationValidity;
  }

  public setMyStyles(index: number) {
    return index === this.getIndex ? "table-selected-cell" : "table-cell";
  }

  async save() {
    try {
      if (
        this.completed &&
        (!this.preAuthorizationRequired || this.preAuthorization)
      ) {
        this.loading = true;
        this.onObservationChange(
          this.serviceReservationsArrayGroup.controls[0].value.Observation
        );
        this.onCredentialNumberChange(
          this.serviceReservationsArrayGroup.controls[0].value.CredentialNumber
        );
        this.onSecurityCodeCredentialNumberChange(
          this.serviceReservationsArrayGroup.controls[0].value.SecurityCodeCredentialNumber
        );
        this.onDiagnosticChange(
          this.serviceReservationsArrayGroup.controls[0].value.Diagnostic
        );
        this.onDateChange(
          this.serviceReservationsArrayGroup.controls[0].value.DateOrder
        );
        this.setServiceReservation();
        await this.reservationWizardService.finalizeReservation();
      }

      if (this.preAuthorizationRequired && !this.preAuthorization) {
        this.notificationToastService.error(
          "Debe realizar la pre autorización",
          "Reserva de turno"
        );
      }
    } catch (err) {
      this.notificationToastService.success(
        "Error al finalizar reserva " + JSON.stringify(err.error),
        "Reserva de turno"
      );
    } finally {
      this.loading = false;
    }
  }

  private setServiceReservation(): void {
    this.serviceReservationsArrayGroup.controls.forEach((control) => {
      const serviceReservation = new ServiceReservation(
        <ServiceReservationInfo>control.value
      );
      this.reservationWizardService.setServiceReservationOrderInfo(
        serviceReservation
      );
    });
  }
}
